import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ColorType,
  createChart,
  CrosshairMode,
  IChartApi,
  ISeriesApi,
  LineStyle,
  LogicalRange,
  UTCTimestamp,
} from "lightweight-charts";
import { TEXT_COLORS } from "../../constants/design/colors";
import { BORDER_RADIUS, COMPONENTS } from "../../constants/design/spacing";
import {
  CHART_RESOLUTIONS,
  readStoredResolution,
  storeResolution,
} from "../../constants/chart/resolutions";
import { areDevOverridesAllowed } from "../../config/features/devOverrides";
import { SettingsContext } from "../../contexts/SettingsContext";
import { ChartBar, useCandles } from "../../hooks/api/candles/useCandles";
import useCandlesWSS from "../../hooks/wss/useCandlesWSS";
import { getAssetShortName } from "../../utils/asset/assets";
import { tripletToHex } from "../../utils/directionColors";
import type { ITradingViewChartProps } from "./LightweightCandles";
import { Container } from "./style";

// ── Native candle source ────────────────────────────────────────────────────
// Every bar here comes from THIS platform's own fills: the indexer aggregates
// OrderMatched events into OHLCV buckets (the same path for spot and perp), the
// backend serves them at GET /candles, and live updates arrive on the `candles`
// websocket channel. Nothing here talks to TradingView, Binance or any other
// venue.
//
// Consequence worth understanding before reading the chart: a period with no
// trades has NO bar. Nothing is forward-filled anywhere in the stack, so a
// young or quiet market shows a handful of candles with gaps between them,
// which is an honest picture of its order flow rather than a flat line at the
// last price.
//
// lightweight-charts is pinned at v4 here, so this uses `addCandlestickSeries`
// (v5's API is `addSeries(CandlestickSeries, ...)`) and v4's built-in
// `watermark` option.

/** How close to the left edge a pan must get before older bars are fetched. */
const PAGE_TRIGGER_BARS = 10;

/**
 * Read a `--color-*` CSS variable.
 *
 * v4 cannot parse the CSS Color-4 space syntax (`rgb(22 199 132)`) that the
 * theme exposes, so the triplet is converted to hex, which v4 does parse.
 */
function readTriplet(name: string, fallback: string): string {
  const raw =
    typeof window !== "undefined"
      ? getComputedStyle(document.documentElement).getPropertyValue(name).trim()
      : "";
  return raw || fallback;
}

function directionColors() {
  return {
    up: tripletToHex(readTriplet("--color-up", "22 199 132")),
    down: tripletToHex(readTriplet("--color-down", "234 57 67")),
  };
}

function candleColors() {
  const { up, down } = directionColors();
  return {
    upColor: up,
    downColor: down,
    borderUpColor: up,
    borderDownColor: down,
    wickUpColor: up,
    wickDownColor: down,
    borderVisible: false,
  };
}

/** Volume bars sit under the price, tinted by the bar's own direction. */
function volumeColor(bar: ChartBar, up: string, down: string): string {
  return `${bar.close >= bar.open ? up : down}66`;
}

function NativeCandlesChart({
  symbol,
  isMobileScreen,
  hidden,
  load = true,
  priceDecimals = 2,
}: ITradingViewChartProps) {
  const { directionScheme, customDirection } = useContext(SettingsContext);
  const [resolution, setResolution] = useState<string>(readStoredResolution);

  const {
    bars,
    isLoading,
    error,
    loadOlder,
    applyLiveBar,
    reachedStart,
    seriesKey,
  } = useCandles(load ? symbol : undefined, resolution);

  useCandlesWSS(load ? symbol : undefined, resolution, applyLiveBar);

  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const volumeRef = useRef<ISeriesApi<"Histogram"> | null>(null);

  /**
   * Bumped every time the chart is rebuilt.
   *
   * The data effect must re-run then, or a rebuild triggered by something the
   * data effect does not watch (a `priceDecimals` change from an instruments
   * refresh, say) leaves an empty chart with no way to notice: the refs point
   * at fresh, dataless series and `bars` never changed.
   */
  const [chartGeneration, setChartGeneration] = useState(0);
  // The series key we have already auto-fitted, so a live tick does not yank
  // the viewport back from wherever the user panned.
  const fittedKeyRef = useRef<string>("");
  // Last visible logical range, to tell a user pan from the auto-fit itself.
  const lastRangeFromRef = useRef<number | null>(null);

  const watermark = useMemo(
    () => (getAssetShortName(symbol) || symbol || "").toUpperCase(),
    [symbol]
  );

  // Build the chart. Kept separate from data so panning and live ticks do not
  // tear down and rebuild the canvas.
  useEffect(() => {
    if (!containerRef.current || !load) return undefined;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: "#0e0e14" },
        textColor: "#9598a1",
        fontSize: 12,
        fontFamily: "'Trebuchet MS', Roboto, Ubuntu, sans-serif",
        // Drop the lightweight-charts attribution logo that links out to
        // tradingview.com — this chart is not their data.
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: "rgba(255, 255, 255, 0.05)" },
        horzLines: { color: "rgba(255, 255, 255, 0.05)" },
      },
      rightPriceScale: {
        borderColor: "rgba(255, 255, 255, 0.08)",
        scaleMargins: { top: 0.08, bottom: 0.24 },
      },
      timeScale: {
        borderColor: "rgba(255, 255, 255, 0.08)",
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 4,
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: "#758696",
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: "#363a45",
        },
        horzLine: {
          color: "#758696",
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: "#363a45",
        },
      },
      watermark: {
        visible: true,
        text: watermark,
        color: "rgba(255, 255, 255, 0.05)",
        fontSize: 46,
        fontStyle: "bold",
        horzAlign: "center",
        vertAlign: "center",
      },
    });

    const series = chart.addCandlestickSeries({
      ...candleColors(),
      priceFormat: {
        type: "price",
        precision: priceDecimals,
        minMove: 1 / Math.pow(10, priceDecimals),
      },
    });

    const volume = chart.addHistogramSeries({
      priceFormat: { type: "volume" },
      priceScaleId: "volume",
    });
    chart.priceScale("volume").applyOptions({
      scaleMargins: { top: 0.82, bottom: 0 },
    });

    chartRef.current = chart;
    seriesRef.current = series;
    volumeRef.current = volume;
    fittedKeyRef.current = "";
    lastRangeFromRef.current = null;
    setChartGeneration((n) => n + 1);

    return () => {
      try {
        chart.remove();
      } catch {
        /* already torn down */
      }
      chartRef.current = null;
      seriesRef.current = null;
      volumeRef.current = null;
    };
  }, [load, priceDecimals, watermark]);

  // Push the series into the chart whenever the data, the chart instance, or
  // the colour scheme changes. Volume colours are baked in at setData time, so
  // a re-theme has to come through here too.
  useEffect(() => {
    const series = seriesRef.current;
    const volume = volumeRef.current;
    if (!series || !volume) return;

    const { up, down } = directionColors();

    series.setData(
      bars.map((b) => ({
        time: b.time as UTCTimestamp,
        open: b.open,
        high: b.high,
        low: b.low,
        close: b.close,
      }))
    );
    volume.setData(
      bars.map((b) => ({
        time: b.time as UTCTimestamp,
        value: b.volume,
        color: volumeColor(b, up, down),
      }))
    );

    // QA handle: the bars that were actually handed to the chart, so a browser
    // test can compare what is DRAWN against what /candles returned rather than
    // just asserting the request went out. Gated on the same switch as the
    // feature-flag overrides, whose operands are compile-time literals — in a
    // normal production build this folds to `false` and Terser drops the block.
    if (areDevOverridesAllowed()) {
      (window as unknown as Record<string, unknown>).__nativeChartBars = bars.map(
        (b) => ({
          time: b.time,
          open: b.open,
          high: b.high,
          low: b.low,
          close: b.close,
          volume: b.volume,
        })
      );
    }

    // Fit once per loaded series. `bars` is emptied when the market or
    // resolution changes, so reaching here with bars means THIS series' data
    // has landed — fitting to the previous one and calling it done was how a
    // timeframe switch used to leave the viewport at the old zoom.
    if (bars.length > 0 && fittedKeyRef.current !== seriesKey) {
      fittedKeyRef.current = seriesKey;
      chartRef.current?.timeScale().fitContent();
      // fitContent puts `from` at ~0, which is inside the paging threshold.
      // Record it so the range subscription can tell it from a real pan.
      lastRangeFromRef.current = null;
    }
  }, [bars, chartGeneration, customDirection, directionScheme, seriesKey]);

  // Re-theme the candles themselves.
  useEffect(() => {
    seriesRef.current?.applyOptions(candleColors());
  }, [chartGeneration, customDirection, directionScheme]);

  // Page in older history as the user pans left.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || reachedStart) return undefined;

    const timeScale = chart.timeScale();
    const handler = (range: LogicalRange | null) => {
      if (!range) return;
      const previous = lastRangeFromRef.current;
      lastRangeFromRef.current = range.from;

      // `from` is a logical index; small or negative means the viewport is at
      // the oldest loaded bar. On its own that is also true right after
      // fitContent(), which would fetch a second page nobody asked for — so
      // require that the user actually moved LEFT since the last event.
      if (previous === null || range.from >= previous) return;
      if (range.from < PAGE_TRIGGER_BARS) void loadOlder();
    };
    timeScale.subscribeVisibleLogicalRangeChange(handler);
    return () => timeScale.unsubscribeVisibleLogicalRangeChange(handler);
  }, [chartGeneration, loadOlder, reachedStart]);

  const onSelectResolution = useCallback((id: string) => {
    setResolution(id);
    storeResolution(id);
  }, []);

  // Bars win over an error: if the REST load failed but the live channel is
  // delivering, the chart is drawing real candles and saying otherwise would be
  // wrong.
  const status = (() => {
    if (bars.length > 0) return null;
    if (isLoading) return "Loading…";
    if (error) return "Chart data unavailable";
    return "No trades yet";
  })();

  return (
    <Container
      data-testid="native-candles-chart"
      style={{
        height: isMobileScreen ? `${COMPONENTS.mobileTvChart}px` : "100%",
        opacity: hidden ? 0 : 1,
        pointerEvents: hidden ? "none" : "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 4,
          padding: "6px 8px",
          flex: "0 0 auto",
          alignItems: "center",
        }}
      >
        {CHART_RESOLUTIONS.map((it) => (
          <button
            key={it.id}
            type="button"
            onClick={() => onSelectResolution(it.id)}
            style={{
              padding: "2px 8px",
              fontSize: 12,
              cursor: "pointer",
              borderRadius: BORDER_RADIUS.one,
              border: "none",
              background:
                resolution === it.id
                  ? "rgb(var(--color-primary, 76 215 244) / 0.16)"
                  : "transparent",
              color: resolution === it.id ? TEXT_COLORS.one : TEXT_COLORS.three,
            }}
          >
            {it.label}
          </button>
        ))}
        {status && (
          <span
            style={{
              marginLeft: "auto",
              fontSize: 12,
              color: TEXT_COLORS.three,
            }}
          >
            {status}
          </span>
        )}
      </div>
      <div
        ref={containerRef}
        data-testid="native-candles-canvas"
        style={{ flex: "1 1 auto", minHeight: 0 }}
      />
    </Container>
  );
}

export default NativeCandlesChart;
